<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { appState } from '../store';
import { TimeFormat } from '@mimir/common/util/DateUtil';

const props = defineProps<{
  value?: string;
}>();

const emit = defineEmits<{
  (e: 'input', value: string): void;
  (e: 'enter'): void;
}>();

const hourInput = ref<HTMLInputElement>();
const minuteInput = ref<HTMLInputElement>();
const periodSwitch = ref<HTMLElement>();

const hourDisplay = ref('');
const minuteDisplay = ref('');
const period = ref<'AM' | 'PM'>('AM');
const currentSegment = ref<'hour' | 'minute' | 'period' | null>(null);

const effectiveFormat = computed<'24h' | '12h'>(() => {
  return appState.dateHelper.getEffectiveTimeFormat();
});

watch(() => props.value, (newValue) => {
  if (currentSegment.value === null) {
    parseValueToSegments(newValue || '');
  }
}, { immediate: true });

watch(effectiveFormat, () => {
  parseValueToSegments(props.value || '');
});

function validateNumericInput(newValue: string, currentValue: string, input: HTMLInputElement): boolean {
  if (!/^\d*$/.test(newValue)) {
    input.value = currentValue;
    return false;
  }
  return true;
}

function formatSegment(
  value: string,
  maxValue: number,
  setValue: (val: string) => void
): void {
  if (!value) {
    setValue('00');
  } else if (value.length === 1 && /^\d$/.test(value)) {
    setValue(value.padStart(2, '0'));
  } else if (value.length === 1) {
    // Non-numeric single character, default to '00'
    setValue('00');
  } else {
    const num = parseInt(value, 10);
    if (num > maxValue) {
      setValue(maxValue.toString().padStart(2, '0'));
    }
  }
  currentSegment.value = null;
  emitCurrentValue();
}

function handleCommonKeydown(event: KeyboardEvent, onEnter: () => void): boolean {
  if (event.key === 'Enter') {
    event.stopPropagation();
    onEnter();
    emit('enter');
    return true;
  }

  if (handlePeriodKey(event)) {
    return true;
  }

  return false;
}

function isAllowedKey(key: string): boolean {
  const allowedKeys = ['Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
  return allowedKeys.includes(key) || (key >= '0' && key <= '9');
}

// To mimic the input type="time" behaviour
function handleBackspaceKey(
  event: KeyboardEvent,
  currentValue: string,
  setValue: (val: string) => void,
  onEmpty?: () => void
): void {
  event.preventDefault();

  if (!currentValue && onEmpty) {
    onEmpty();
    return;
  }

  if (currentValue) {
    setValue(currentValue.slice(0, -1));
    emitCurrentValue();

    nextTick(() => {
      const input = event.target as HTMLInputElement;
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }
}

function parseValueToSegments(timeString: string) {
  if (!timeString) {
    hourDisplay.value = '';
    minuteDisplay.value = '';
    period.value = 'AM';
    return;
  }

  // Parse HH:mm format (always in 24h internally)
  const [hoursStr, minsStr] = timeString.split(':');
  const hours24 = parseInt(hoursStr, 10);
  const mins = parseInt(minsStr, 10);

  if (isNaN(hours24) || isNaN(mins)) {
    hourDisplay.value = '';
    minuteDisplay.value = '';
    period.value = 'AM';
    return;
  }

  minuteDisplay.value = mins.toString().padStart(2, '0');

  if (effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR) {
    hourDisplay.value = hours24.toString().padStart(2, '0');
  } else if (effectiveFormat.value === TimeFormat.TWELVE_HOUR) {
    if (hours24 === 0) {
      hourDisplay.value = '12';
      period.value = 'AM';
    } else if (hours24 < 12) {
      hourDisplay.value = hours24.toString().padStart(2, '0');
      period.value = 'AM';
    } else if (hours24 === 12) {
      hourDisplay.value = '12';
      period.value = 'PM';
    } else {
      hourDisplay.value = (hours24 - 12).toString().padStart(2, '0');
      period.value = 'PM';
    }
  }
}

function onContainerClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (target.classList.contains('time-input-container') || target.classList.contains('time-separator')) {
    focusFirstSegment();
  }
}

function focusFirstSegment() {
  if (hourInput.value) {
    hourInput.value.focus();
    hourInput.value.select();
  }
}

function incrementHour() {
  const currentHour = parseInt(hourDisplay.value, 10) || 0;
  const maxHour = effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR ? 23 : 12;
  const minHour = effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR ? 0 : 1;

  let newHour: number;
  if (currentHour >= maxHour) {
    newHour = minHour;
  } else {
    newHour = currentHour + 1;
  }

  hourDisplay.value = newHour.toString().padStart(2, '0');
  emitCurrentValue();
  nextTick(() => {
    hourInput.value?.select();
  });
}

function decrementHour() {
  const currentHour = parseInt(hourDisplay.value, 10) || 0;
  const maxHour = effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR ? 23 : 12;
  const minHour = effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR ? 0 : 1;

  let newHour: number;
  if (currentHour <= minHour) {
    newHour = maxHour;
  } else {
    newHour = currentHour - 1;
  }

  hourDisplay.value = newHour.toString().padStart(2, '0');
  emitCurrentValue();
  nextTick(() => {
    hourInput.value?.select();
  });
}

function incrementMinute() {
  const currentMinute = parseInt(minuteDisplay.value, 10) || 0;

  const newMinute = currentMinute >= 59 ? 0 : currentMinute + 1;

  minuteDisplay.value = newMinute.toString().padStart(2, '0');
  emitCurrentValue();
  nextTick(() => {
    minuteInput.value?.select();
  });
}

function decrementMinute() {
  const currentMinute = parseInt(minuteDisplay.value, 10) || 0;

  const newMinute = currentMinute <= 0 ? 59 : currentMinute - 1;

  minuteDisplay.value = newMinute.toString().padStart(2, '0');
  emitCurrentValue();
  nextTick(() => {
    minuteInput.value?.select();
  });
}

function selectSegment(segment: 'hour' | 'minute' | 'period') {
  currentSegment.value = segment;
  if (segment === 'hour') {
    if (hourInput.value) {
      if (hourInput.value.value.length === 1) {
        hourInput.value.setSelectionRange(1, 1);
      } else {
        hourInput.value.select();
      }
    }
  } else if (segment === 'minute') {
    if (minuteInput.value) {
      if (minuteInput.value.value.length === 1) {
        minuteInput.value.setSelectionRange(1, 1);
      } else {
        minuteInput.value.select();
      }
    }
  }
}

function onHourInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const newValue = input.value;

  if (!validateNumericInput(newValue, hourDisplay.value, input)) {
    return;
  }

  if (newValue.length > 2) {
    const hourPart = newValue.substring(0, 2);
    const minutePart = newValue.substring(2);

    let hours = parseInt(hourPart, 10);
    if (effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR) {
      if (hours > 23) {
        hours = 23;
      }
    } else {
      if (hours < 1) {
        hours = 1;
      }
      if (hours > 12) {
        hours = 12;
      }
    }
    hourDisplay.value = hours.toString().padStart(2, '0');

    nextTick(() => {
      if (minuteInput.value) {
        minuteDisplay.value = minutePart.substring(0, 2);
        minuteInput.value.focus();
        minuteInput.value.setSelectionRange(0, minuteDisplay.value.length);
      }
    });
    emitCurrentValue();
    return;
  }

  if (effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR && newValue.length === 2) {
    const num = parseInt(newValue, 10);
    const clamped = (num > 23 ? 23 : num).toString().padStart(2, '0');
    hourDisplay.value = clamped;
    nextTick(() => {
      if (minuteInput.value) {
        minuteInput.value.focus();
        minuteInput.value.select();
      }
    });
    emitCurrentValue();
    return;
  }

  if (effectiveFormat.value === TimeFormat.TWELVE_HOUR && newValue.length === 2) {
    let num = parseInt(newValue, 10);
    if (num < 1) {
      num = 1;
    }
    if (num > 12) {
      num = 12;
    }
    hourDisplay.value = num.toString().padStart(2, '0');
    nextTick(() => {
      if (minuteInput.value) {
        minuteInput.value.focus();
        minuteInput.value.select();
      }
    });
    emitCurrentValue();
    return;
  }

  hourDisplay.value = newValue;
}

function onHourKeydown(event: KeyboardEvent) {
  if (handleCommonKeydown(event, formatHour)) {
    return;
  }

  if (event.key === 'Backspace') {
    handleBackspaceKey(event, hourDisplay.value, (val) => { hourDisplay.value = val; });
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    incrementHour();
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    decrementHour();
    return;
  }

  if (!isAllowedKey(event.key)) {
    event.preventDefault();
    return;
  }

  if (event.key === 'ArrowRight' || event.key === ':') {
    event.preventDefault();
    if (minuteInput.value) {
      minuteInput.value.focus();
      minuteInput.value.select();
    }
  }
}

function formatHour() {
  const maxHour = effectiveFormat.value === TimeFormat.TWENTY_FOUR_HOUR ? 23 : 12;
  formatSegment(hourDisplay.value, maxHour, (val) => { hourDisplay.value = val; });
}

function onMinuteInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const newValue = input.value;

  if (!validateNumericInput(newValue, minuteDisplay.value, input)) {
    return;
  }

  if (newValue.length === 2) {
    const num = parseInt(newValue, 10);
    const clamped = (num > 59 ? 59 : num).toString().padStart(2, '0');
    minuteDisplay.value = clamped;

    nextTick(() => {
      input.setSelectionRange(0, 2);
    });

    emitCurrentValue();
    return;
  }

  // For length 0 or 1, always update the display
  minuteDisplay.value = newValue;
}

function onMinuteKeydown(event: KeyboardEvent) {
  const input = event.target as HTMLInputElement;

  if (handleCommonKeydown(event, formatMinute)) {
    return;
  }

  if (event.key === 'Backspace') {
    const onEmpty = () => {
      if (hourInput.value && hourDisplay.value) {
        hourDisplay.value = hourDisplay.value.slice(0, -1);
        emitCurrentValue();
        hourInput.value.focus();
        nextTick(() => {
          hourInput.value?.setSelectionRange(hourInput.value.value.length, hourInput.value.value.length);
        });
      }
    };
    handleBackspaceKey(event, minuteDisplay.value, (val) => { minuteDisplay.value = val; }, onEmpty);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    incrementMinute();
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    decrementMinute();
    return;
  }

  const isDigit = event.key >= '0' && event.key <= '9';

  // To mimic the input type="time" behaviour, digits "roll" like in a queue where the first pop out
  if (isDigit && minuteDisplay.value.length === 2) {
    event.preventDefault();
    const newValue = minuteDisplay.value.charAt(1) + event.key;
    minuteDisplay.value = newValue;
    nextTick(() => {
      input.setSelectionRange(0, 2);
    });
    emitCurrentValue();
    return;
  }

  if (!isAllowedKey(event.key)) {
    event.preventDefault();
    return;
  }

  if (event.key === 'ArrowLeft') {
    if (input.selectionStart === 0) {
      event.preventDefault();
      if (hourInput.value) {
        hourInput.value.focus();
        hourInput.value.select();
      }
    }
  }
  if (event.key === 'ArrowRight' && effectiveFormat.value === TimeFormat.TWELVE_HOUR) {
    if (input.selectionStart === input.value.length) {
      event.preventDefault();
      if (periodSwitch.value) {
        periodSwitch.value.focus();
      }
    }
  }
}

function formatMinute() {
  formatSegment(minuteDisplay.value, 59, (val) => { minuteDisplay.value = val; });
}

function setPeriod(newPeriod: 'AM' | 'PM') {
  if (period.value !== newPeriod) {
    period.value = newPeriod;
    emitCurrentValue();
  }
}

function togglePeriod() {
  period.value = period.value === 'AM' ? 'PM' : 'AM';
  emitCurrentValue();
}

function onPeriodKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.stopPropagation();
    emit('enter');
    return;
  }

  if (handlePeriodKey(event)) {
    return;
  }

  if (event.key === ' ' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    event.preventDefault();
    event.stopPropagation();
    togglePeriod();
    return;
  }
}

function handlePeriodKey(event: KeyboardEvent) {
  if ((event.key === 'a' || event.key === 'A') && effectiveFormat.value === TimeFormat.TWELVE_HOUR) {
    event.preventDefault();
    event.stopPropagation();
    setPeriod('AM');
    return true;
  }

  if ((event.key === 'p' || event.key === 'P') && effectiveFormat.value === TimeFormat.TWELVE_HOUR) {
    event.preventDefault();
    event.stopPropagation();
    setPeriod('PM');
    return true;
  }

  return false;
}

function emitCurrentValue() {
  const hoursRaw = hourDisplay.value;
  let hours24 = parseInt(hoursRaw, 10);
  const minutes = parseInt(minuteDisplay.value, 10) || 0;

  if (effectiveFormat.value === TimeFormat.TWELVE_HOUR) {
    // Handle empty or non-numeric hour field
    if (!hoursRaw || isNaN(hours24)) {
      hours24 = period.value === 'PM' ? 12 : 0;
    } else if (period.value === 'PM' && hours24 !== 12) {
      hours24 += 12;
    } else if (period.value === 'AM' && hours24 === 12) {
      hours24 = 0;
    }
  } else if (!hoursRaw || isNaN(hours24)) {
    // For 24h format, default to 0 if empty or non-numeric
    hours24 = 0;
  }

  const formatted = `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  emit('input', formatted);
}
</script>

<template>
  <div>
    <label>{{ $t('MTimeInput.SelectTime') }}</label>
    <div class="time-input-wrapper">
      <div
        class="time-input-container"
        :aria-label="$t('MTimeInput.SelectTime')"
        @click="onContainerClick"
      >
        <input
          ref="hourInput"
          v-model="hourDisplay"
          type="text"
          inputmode="numeric"
          class="time-segment"
          aria-label="Hour"
          maxlength="2"
          @input="onHourInput"
          @keydown="onHourKeydown"
          @focus="selectSegment('hour')"
          @blur="formatHour"
          @click.stop="selectSegment('hour')"
        >
        <span class="time-separator">:</span>
        <input
          ref="minuteInput"
          v-model="minuteDisplay"
          type="text"
          inputmode="numeric"
          class="time-segment"
          aria-label="Minute"
          maxlength="2"
          @input="onMinuteInput"
          @keydown="onMinuteKeydown"
          @focus="selectSegment('minute')"
          @blur="formatMinute"
          @click.stop="selectSegment('minute')"
        >
      </div>
      <div
        v-if="effectiveFormat === TimeFormat.TWELVE_HOUR"
        ref="periodSwitch"
        class="period-switch"
        tabindex="0"
        role="radiogroup"
        aria-label="AM or PM"
        @focus="selectSegment('period')"
        @keydown="onPeriodKeydown"
      >
        <button
          type="button"
          role="radio"
          aria-label="AM"
          :aria-checked="period === 'AM'"
          :class="['period-option', { active: period === 'AM' }]"
          @click.stop="setPeriod('AM')"
        >
          AM
        </button>
        <button
          type="button"
          role="radio"
          aria-label="PM"
          :aria-checked="period === 'PM'"
          :class="['period-option', { active: period === 'PM' }]"
          @click.stop="setPeriod('PM')"
        >
          PM
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
label {
  font-size: 10px;
  font-weight: 500;
  color: var(--v-label-base) !important;
  margin: 4px 0 0 0;
  display: block;
}

.time-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.time-input-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 6px 8px;
  border: 1px solid var(--v-inputBorder-lighten2);
  border-radius: 4px;
  background: var(--v-inputBackground-base) !important;
  cursor: text;
  font-size: 12px;
  height: 34px;
  width: fit-content;
  transition: border-color 0.2s;
  color: inherit;

  &:hover {
    border-color: var(--v-inputBorder-lighten2);
  }

  &:focus-within {
    border-color: var(--v-primary-base);
    border-width: 2px;
    padding: 5px 7px;
  }
}

.time-segment {
  border: none;
  outline: none;
  background: transparent;
  padding: 2px 1px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  font-family: inherit;
  color: inherit;
  width: 18px;
  transition: background-color 0.15s, color 0.15s;

  &:focus {
    background: var(--v-primary-base);
    color: white;
    border-radius: 2px;
  }
}

.time-separator {
  font-weight: normal;
  color: inherit;
  user-select: none;
  padding: 0 1px;
  font-size: 12px;
  cursor: text;
}

.period-switch {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid var(--v-inputBorder-lighten2);
  border-radius: 4px;
  padding: 2px;
  background: var(--v-inputBackground-base);
  outline: none;
  height: 34px;

  &:focus {
    border-color: var(--v-primary-base);
  }
}

.period-option {
  border: none;
  background: transparent;
  padding: 4px 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: inherit;
  border-radius: 2px;
  transition: background-color 0.15s, color 0.15s;
  min-width: 28px;
  height: 100%;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    background: var(--v-primary-base);
    color: white;
    opacity: 1;
  }

  &:focus {
    outline: none;
  }
}
</style>
